import React, { useEffect, useCallback, useState } from 'react';
import { VideoOff, Video, Blend, SquareUserRound } from 'lucide-react';
import { useVideoCall } from '../../hooks/useVideoCall';
import type { ChimeMeetingInfo, VideoCapabilities } from '../../types/amazon-connect-streams';

interface VideoCallPanelProps {
    videoConnectionInfo: ChimeMeetingInfo;
    videoCapabilities: VideoCapabilities;
    isOpen: boolean;
    isOnHold: boolean;
    onClose: () => void;
}

export const VideoCallPanel: React.FC<VideoCallPanelProps> = ({
    videoConnectionInfo,
    videoCapabilities,
    isOpen,
    isOnHold,
    onClose,
}) => {
    const [cameraPermissionError, setCameraPermissionError] = useState<string | null>(null);

    const {
        isCameraOn,
        isBlurOn,
        remoteTileIds,
        videoError,
        joinVideoMeeting,
        leaveVideoMeeting,
        toggleCamera,
        toggleBlur,
        localVideoRef,
        bindRemoteTileElement,
    } = useVideoCall();

    // Join on mount, leave on unmount — connection is established even before panel is opened
    // so video is live by the time the user expands the panel.
    useEffect(() => {
        joinVideoMeeting(videoConnectionInfo);
        return () => {
            leaveVideoMeeting();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatCameraPermissionError = (err: unknown): string => {
        const anyErr = err as any;
        const name = anyErr?.name ? String(anyErr.name) : '';

        if (name === 'NotAllowedError' || name === 'SecurityError') {
            return 'Camera permission is blocked. Please allow camera access for this site in your browser settings, then try again.';
        }
        if (name === 'NotFoundError') {
            return 'No camera device was found. Please connect a camera and try again.';
        }
        if (name === 'NotReadableError') {
            return 'Camera is currently in use by another application. Close other apps using the camera and try again.';
        }
        return 'Unable to access camera. Please check browser permissions and try again.';
    };

    const handleCameraClick = useCallback(async () => {
        setCameraPermissionError(null);

        // Force the browser permission popup on user click (most reliable).
        if (!isCameraOn) {
            if (!navigator?.mediaDevices?.getUserMedia) {
                setCameraPermissionError('Camera access is not supported in this browser.');
                return;
            }

            try {
                // If the user previously denied permission, Chrome often won't reprompt.
                // The Permissions API lets us show a clearer message.
                if (navigator.permissions?.query) {
                    try {
                        const status = await navigator.permissions.query({name: 'camera' as PermissionName});
                        if (status.state === 'denied') {
                            setCameraPermissionError(
                                'Camera permission is blocked for this site. Please change it to Allow in your browser site settings, then try again.'
                            );
                            return;
                        }
                    } catch {
                        // ignore Permissions API failures
                    }
                }

                const stream = await navigator.mediaDevices.getUserMedia({video: true});
                stream.getTracks().forEach((t) => t.stop());
            } catch (err) {
                setCameraPermissionError(formatCameraPermissionError(err));
                return;
            }
        }
        await toggleCamera();
    }, [isCameraOn, toggleCamera]);

    // Turn off local camera when the call is placed on hold.
    useEffect(() => {
        if (isOnHold && isCameraOn) {
            toggleCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnHold]);

    // Ref callback: stores + immediately binds each remote tile element
    const bindRemoteTile = useCallback(
        (tileId: number) => (el: HTMLVideoElement | null) => {
            bindRemoteTileElement(tileId, el);
        },
        [bindRemoteTileElement]
    );

    return (
        <div className={`cvp-side-panel ${isOpen ? 'open' : ''}`}>
            <div className="cvp-panel">

                {/* Video area — dark background */}
                <div className="cvp-remote-area">
                    {remoteTileIds.length === 0 ? (
                        <div className="cvp-placeholder">
                            <VideoOff size={26} strokeWidth={1.5} color="#ffffff" />
                            <span className="cvp-placeholder-text">No Video</span>
                        </div>
                    ) : (
                        remoteTileIds.map(tileId => (
                            <video
                                key={tileId}
                                className="cvp-remote-tile"
                                ref={bindRemoteTile(tileId)}
                                autoPlay
                                playsInline
                            />
                        ))
                    )}

                    {/* Local video — PiP in bottom-right of video area */}
                    {isCameraOn && (
                        <video
                            className="cvp-local-tile"
                            ref={localVideoRef}
                            autoPlay
                            playsInline
                            muted
                        />
                    )}

                    {/* Non-blocking error overlay — sits at bottom, doesn't replace video */}
                    {(cameraPermissionError || videoError) && (
                        <div className="cvp-error-overlay">
                            <span>{cameraPermissionError ?? videoError}</span>
                        </div>
                    )}
                </div>

                {/* Controls footer — white, matches CCP footer style */}
                {videoCapabilities.canSendVideo && (
                    <div className="cvp-controls">
                        <div className="d-flex flex-column align-items-center gap-1">
                            <button
                                type="button"
                                className={`cw-circle-btn ${isCameraOn ? 'active' : ''}`}
                                onClick={handleCameraClick}
                                aria-label={isCameraOn ? 'Turn camera off' : 'Turn camera on'}
                                aria-pressed={isCameraOn}
                                title={isCameraOn ? 'Turn camera off' : 'Turn camera on'}
                            >
                                {isCameraOn ? <VideoOff size={18} aria-hidden="true" /> : <Video size={18} aria-hidden="true" />}
                            </button>
                            <div className="cw-btn-label" aria-hidden="true">{isCameraOn ? 'Cam Off' : 'Camera'}</div>
                        </div>

                        <div className="d-flex flex-column align-items-center gap-1">
                            <button
                                type="button"
                                className={`cw-circle-btn ${isBlurOn ? 'active' : ''}`}
                                onClick={toggleBlur}
                                aria-label={isBlurOn ? 'Disable background blur' : 'Enable background blur'}
                                aria-pressed={isBlurOn}
                                title={isBlurOn ? 'Disable background blur' : 'Enable background blur'}
                            >
                                <SquareUserRound size={18} aria-hidden="true" />
                            </button>
                            <div className="cw-btn-label" aria-hidden="true">Background Blur</div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default VideoCallPanel;
